<template>
  <div>
    <ActionMenu
      :key="`am-${closeSignal}`"
      :items="mappedItems"
      :icon="icon"
      :size="size"
      :flat="flat"
      :round="round"
      :dense="dense"
      :buttonColor="buttonColor"
      :menuContentClass="menuContentClass"
      :menuContentStyle="menuContentStyle"
      @action="onAction"
      @open="$emit('open')"
      @close="$emit('close')"
    />
  </div>
</template>

<script>
  import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
  import ActionMenu from '@/components/ui/menu/ActionMenu.vue'

  export default defineComponent({
    name: 'PluginActionMenu',
    components: { ActionMenu },
    props: {
      items: { type: Array, default: () => [] },
      // Whether the target UI is currently fullscreen; used to swap label/icon.
      // If omitted (null), the component will auto-detect via Fullscreen API.
      isFullscreen: { type: Boolean, default: null },
      // Current layer visibility state so checkboxes can reflect it
      layerVisibility: {
        type: Object,
        default: () => ({
          background: true,
          camera: false,
          avatarFace: true,
          avatarHair: true,
          avatarImage: true
        })
      },
      // Whether camera background removal (segmentation) is enabled
      segmentationEnabled: { type: Boolean, default: false },
      // Whether any avatar is currently selected
      hasAvatar: { type: Boolean, default: false },
      // Whether the avatar has a detected face/rig (controls visibility of face-related options)
      avatarHasFaceRig: { type: Boolean, default: true },
      // Key used to identify the fullscreen item when custom items are supplied
      fullscreenKey: { type: String, default: 'fullscreen' },
      // Auto-detect document fullscreen state when isFullscreen is null
      detectFullscreen: { type: Boolean, default: true },
      size: { type: String, default: 'md' },
      flat: { type: Boolean, default: false },
      round: { type: Boolean, default: false },
      dense: { type: Boolean, default: false },
      buttonColor: { type: String, default: '' },
      icon: { type: String, default: 'settings' },
      // Optional class to apply to the menu content (for z-index control)
      menuContentClass: { type: String, default: '' },
      // Optional inline style for menu content (force z-index)
      menuContentStyle: { type: String, default: '' },
      // Control visibility of special items in the default menu
      showFullscreen: { type: Boolean, default: true },
      showResize: { type: Boolean, default: true },
      // Token to force-recreate/close ActionMenu on outside clicks that don't bubble
      closeSignal: { type: [Number, String], default: 0 }
    },
    emits: [
      // Back-compat raw action payload
      'action',
      'open',
      'close',
      // Centralized, high-level intents for consumers to v-model or react to
      'update:layerVisibility',
      'update:segmentationEnabled',
      'request:fullscreen',
      'request:pip-size',
      'open:background-picker',
      'open:eye-settings',
      'open:voice-settings',
      'open:refine-selection'
    ],
    setup(props, { emit }) {
      const itemsComputed = computed(() => {
        if (props.items && props.items.length) return props.items

        const items = []

        // Face-related options only shown when avatar has detected face/rig
        if (props.avatarHasFaceRig) {
          items.push({ key: 'eye-settings', label: 'Eye Settings', icon: 'visibility' })
        }

        // Voice settings (always available)
        items.push({ key: 'voice-settings', label: 'Voice Settings', icon: 'settings_voice' })

        items.push({ key: 'add-background', label: 'Change Background', icon: 'wallpaper' })
        items.push({ key: 'refine-selection', label: 'Refine Selection', icon: 'gesture' })
        items.push({
          key: 'divider'
        })
        // Avatar image toggle: show when no face rig is detected, regardless of hasAvatar flag.
        // This ensures users can reveal the flat avatar image for non-detectable faces
        // (dragonborn, aarakocra, masks, etc.).
        if (!props.avatarHasFaceRig) {
          items.push({
            key: 'layer-avatar-image',
            label: 'Show Avatar',
            icon: 'account_circle',
            checkbox: true,
            checked: !!props.layerVisibility?.avatarImage
          })
        }

        // Face-specific layers only when rig detected
        if (props.avatarHasFaceRig) {
          items.push(
            {
              key: 'layer-hair',
              label: 'Show Hair',
              icon: 'face_retouching_natural',
              checkbox: true,
              checked: !!props.layerVisibility?.avatarHair
            },
            {
              key: 'layer-face',
              label: 'Show Face',
              icon: 'face',
              checkbox: true,
              checked: !!props.layerVisibility?.avatarFace
            },
            {
              key: 'layer-eyes',
              label: 'Show Eyes',
              icon: 'remove_red_eye',
              checkbox: true,
              checked: !!props.layerVisibility?.avatarEyes
            }
          )
        }
        items.push({
          key: 'layer-camera',
          label: 'Show Camera',
          icon: 'videocam',
          checkbox: true,
          checked: !!props.layerVisibility?.camera
        })
        items.push(
          {
            key: 'remove-bg',
            label: 'Remove Camera Background',
            icon: 'blur_on',
            checkbox: true,
            checked: !!props.segmentationEnabled
          },
          { key: 'divider' },
          // Fullscreen (optional)
          ...(props.showFullscreen
            ? [{ key: 'fullscreen', label: 'Fullscreen', icon: 'fullscreen' }]
            : []),
          // Resize PiP (optional)
          ...(props.showResize ? [{ key: 'pip-size', label: 'Resize', icon: 'zoom_out_map' }] : [])
        )

        return items
      })

      // Internal fullscreen detection (fallback when prop is not provided)
      const internalFs = ref(false)

      const readFullscreen = () => {
        try {
          // Standard Fullscreen API
          return !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
          )
        } catch {
          return false
        }
      }

      if (props.isFullscreen === null && props.detectFullscreen) {
        const onFsChange = () => {
          internalFs.value = readFullscreen()
        }
        onMounted(() => {
          internalFs.value = readFullscreen()
          if (typeof window !== 'undefined') {
            document.addEventListener('fullscreenchange', onFsChange)
            document.addEventListener('webkitfullscreenchange', onFsChange)
            document.addEventListener('mozfullscreenchange', onFsChange)
            document.addEventListener('MSFullscreenChange', onFsChange)
          }
        })
        onUnmounted(() => {
          document.removeEventListener('fullscreenchange', onFsChange)
          document.removeEventListener('webkitfullscreenchange', onFsChange)
          document.removeEventListener('mozfullscreenchange', onFsChange)
          document.removeEventListener('MSFullscreenChange', onFsChange)
        })
      }

      const currentIsFullscreen = computed(() => {
        return props.isFullscreen !== null ? !!props.isFullscreen : !!internalFs.value
      })

      const mappedItems = computed(() => {
        try {
          return (itemsComputed.value || []).map((it) => {
            if (!it) return it
            if (it.key === 'divider' || it.separator === true) return { separator: true }
            // Determine logical key used by ActionMenu (key | event | id)
            const logicalKey = it.key || it.event || it.id
            // Keep checkbox 'checked' in sync for known layer items
            if (logicalKey === 'layer-camera') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.layerVisibility?.camera
              }
            }
            if (logicalKey === 'layer-hair') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.layerVisibility?.avatarHair
              }
            }
            if (logicalKey === 'layer-face') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.layerVisibility?.avatarFace
              }
            }
            if (logicalKey === 'layer-eyes') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.layerVisibility?.avatarEyes
              }
            }
            if (logicalKey === 'layer-avatar-image') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.layerVisibility?.avatarImage
              }
            }
            if (logicalKey === 'remove-bg') {
              return {
                ...it,
                checkbox: true,
                checked: !!props.segmentationEnabled
              }
            }

            // Swap label/icon for fullscreen item based on current state
            const matchesFullscreen =
              logicalKey === props.fullscreenKey ||
              (typeof it.label === 'string' && it.label.toLowerCase().includes('fullscreen'))

            if (matchesFullscreen && props.showFullscreen) {
              const isFs = currentIsFullscreen.value
              return {
                ...it,
                label: isFs ? 'Exit Fullscreen' : 'Fullscreen',
                icon: isFs ? 'fullscreen_exit' : 'fullscreen'
              }
            }
            // preserve other items
            return { ...it }
          })
        } catch {
          return itemsComputed.value || []
        }
      })

      const onAction = (payload) => {
        try {
          // Debug hook
          try {
            console.log('[PluginActionMenu] onAction', payload)
          } catch {}
          const key = payload?.key || payload?.event || payload?.id || ''
          const checked = typeof payload?.checked === 'boolean' ? payload.checked : undefined

          // Always emit raw payload for back-compat first
          emit('action', payload)

          // Centralized handling with high-level emits (no direct prop mutation)
          if (key === 'eye-settings' || key === 'eye_settings') {
            try {
              console.log('[PluginActionMenu] emitting open:eye-settings')
            } catch {}
            emit('open:eye-settings')
            return
          }
          if (key === 'voice-settings' || key === 'voice_settings' || key === 'voice') {
            try {
              console.log('[PluginActionMenu] emitting open:voice-settings')
            } catch {}
            emit('open:voice-settings')
            return
          }
          if (key === 'add-background' || key === 'background' || key === 'add_bg') {
            try {
              console.log('[PluginActionMenu] emitting open:background-picker')
            } catch {}
            emit('open:background-picker')
            return
          }
          if (key === 'refine-selection' || key === 'refine' || key === 'selection') {
            emit('open:refine-selection')
            return
          }
          if (key === 'fullscreen') {
            emit('request:fullscreen')
            return
          }
          if (key === 'pip-size' || key === 'resize') {
            emit('request:pip-size')
            return
          }

          // Layer toggles
          const nextLv = { ...(props.layerVisibility || {}) }
          if (key === 'layer-camera') {
            const next = typeof checked === 'boolean' ? checked : !nextLv.camera
            nextLv.camera = !!next
            emit('update:layerVisibility', nextLv)
            return
          }
          if (key === 'layer-hair') {
            const next = typeof checked === 'boolean' ? checked : !nextLv.avatarHair
            nextLv.avatarHair = !!next
            emit('update:layerVisibility', nextLv)
            return
          }
          if (key === 'layer-face') {
            const next = typeof checked === 'boolean' ? checked : !nextLv.avatarFace
            nextLv.avatarFace = !!next
            emit('update:layerVisibility', nextLv)
            return
          }
          if (key === 'layer-eyes') {
            const next = typeof checked === 'boolean' ? checked : !nextLv.avatarEyes
            nextLv.avatarEyes = !!next
            emit('update:layerVisibility', nextLv)
            return
          }
          if (key === 'layer-avatar-image') {
            const next = typeof checked === 'boolean' ? checked : !nextLv.avatarImage
            nextLv.avatarImage = !!next
            emit('update:layerVisibility', nextLv)
            return
          }
          if (key === 'remove-bg') {
            const next = typeof checked === 'boolean' ? checked : !props.segmentationEnabled
            emit('update:segmentationEnabled', !!next)
            return
          }
        } catch {
          /* noop */
        }
      }

      return { itemsComputed, mappedItems, onAction }
    }
  })
</script>
